import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

export type NuveiSdkMethod =
  | "createPayment"
  | "getToken"
  | "addCardUpo"
  | "createApplePayPayment";

// Nuvei free-text fields may echo request fragments; strip anything that
// looks like a card number (13-19 digit runs) and keep the text short.
const MAX_TEXT_LENGTH = 256;
const scrubText = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value === "") {
    return undefined;
  }
  return value
    .replace(/\d[\d\s-]{11,}\d/g, "[redacted]")
    .slice(0, MAX_TEXT_LENGTH);
};

const asScalar = (value: unknown): string | number | boolean | undefined =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"
    ? value
    : undefined;

export type NuveiErrorContext = {
  sdkMethod: NuveiSdkMethod;
  status?: string | number | boolean;
  result?: string | number | boolean;
  errCode?: string | number | boolean;
  reason?: string;
  errorDescription?: string;
  threeDReason?: string;
  challengeCancelReason?: string;
  cancelled?: string | number | boolean;
  internalRequestId?: string | number | boolean;
  merchantSiteId?: string | number | boolean;
  clientUniqueId?: string | number | boolean;
  pspTransactionId?: string | number | boolean;
  version?: string | number | boolean;
};

/**
 * Build a PII-safe context from a Nuvei WebSDK response using a strict
 * allowlist. Never spread the raw response: it carries `sessionToken`
 * (credential), `userTokenId` (customer identifier) and card data
 * (bin/last4/expiry), none of which may be shipped to telemetry.
 */
export const toNuveiErrorContext = (
  sdkMethod: NuveiSdkMethod,
  response: unknown
): NuveiErrorContext => {
  const raw = (
    typeof response === "object" && response !== null ? response : {}
  ) as Record<string, unknown>;

  return {
    sdkMethod,
    status: asScalar(raw.status),
    result: asScalar(raw.result),
    errCode: asScalar(raw.errCode),
    reason: scrubText(raw.reason),
    errorDescription: scrubText(raw.errorDescription),
    threeDReason: scrubText(raw.threeDReason),
    challengeCancelReason: scrubText(raw.challengeCancelReason),
    cancelled: asScalar(raw.cancelled),
    internalRequestId: asScalar(raw.internalRequestId),
    merchantSiteId: asScalar(raw.merchantSiteId),
    // Our own payment reference — safe, and correlates with backend logs.
    clientUniqueId: asScalar(raw.clientUniqueId),
    // Nuvei's transaction id (not ours) — safe correlation id on their side.
    pspTransactionId: asScalar(raw.transactionId),
    version: asScalar(raw.version),
  };
};

/**
 * Report a Nuvei WebSDK error (HTTP 200 + status:"ERROR"/non-zero errCode)
 * to Datadog RUM as an error event with allowlisted context.
 */
export const reportNuveiSdkError = (
  sdkMethod: NuveiSdkMethod,
  response: unknown
) => {
  const context = toNuveiErrorContext(sdkMethod, response);
  datadogRum.addError(new Error(`Nuvei WebSDK ${sdkMethod} failed`), {
    event: "nuvei_sdk_error",
    nuvei: context,
  });
  // Also ship via Browser Logs (100% sampled) so errors from RUM-unsampled
  // sessions are still captured.
  datadogLogs.logger.error(`Nuvei WebSDK ${sdkMethod} failed`, {
    event: "nuvei_sdk_error",
    nuvei: context,
  });
  console.error("Nuvei WebSDK error", context);
};

/**
 * TEMP: report a Nuvei WebSDK success to Datadog RUM as a custom action and
 * to Datadog Logs, to verify both instrumentations end-to-end. Remove once
 * verified.
 */
export const reportNuveiSdkSuccess = (
  sdkMethod: NuveiSdkMethod,
  response: unknown
) => {
  const context = toNuveiErrorContext(sdkMethod, response);
  datadogRum.addAction("nuvei_sdk_success", { nuvei: context });
  datadogLogs.logger.info(`Nuvei WebSDK ${sdkMethod} succeeded`, {
    event: "nuvei_sdk_success",
    nuvei: context,
  });
  console.info("Nuvei WebSDK success", context);
};
